import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import type { CandidateActivityLogResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import type { Candidate } from 'model/Candidate/Candidate';
import type { CandidateHistory, CandidateHistoryQueryData } from './useCandidateHistoryQuery.types';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { usePullIndeedMessageBasedOnCandidate } from 'hooks/indeed/usePullIndeedMessageBasedOnCandidate';
import { SyncStatus } from 'services/browser-extension/indeed/indeed.types';
import { CandidateHistoryAction } from 'services/candidates/types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { QueryKey } from 'types/query';
import { sanitizeEmailHtml } from 'utils/sanitizeEmailHtml';
import { useIndeedConfig } from './useBrowserExtension/indeed/useIndeedConfig';
import { extractActivityLogFromResponse } from './useCandidateActivityLogQuery';

export const useCandidateHistoryQuery = (
  candidate?: Candidate | null,
  options: { enabled?: boolean } = { enabled: true },
) => {
  const { language } = useContext(LocalizationContext);
  const queryClient = useQueryClient();
  const indeedConfig = useIndeedConfig();
  const { pullIndeedMessages } = usePullIndeedMessageBasedOnCandidate({
    candidate: candidate,
    indeedConfig,
  });

  const queryResult = useQuery<CandidateHistoryQueryData>(
    [QueryKey.useCandidateHistoryQuery, { candidateEsPersonId: candidate?.es_person_id, status: candidate?.status }],
    async () => {
      const { syncedStatus, syncedAt, hasMessages } = await pullIndeedMessages();

      const { data } = await campaignsClient.getCandidateHistory({
        candidate_id: candidate?.es_person_id,
      });

      const transformedData = data
        .flatMap((history) => {
          if (
            history.action === CandidateHistoryAction.RJCommHubInitialMessageSent &&
            history.channel_notifications &&
            history.channel_notifications.length > 1
          ) {
            return history.channel_notifications.map((channelNotification) => {
              const historyAsActivityLog: CandidateActivityLogResponse = {
                ...history,
                id: channelNotification.id,
                created_at: channelNotification.created_at,
                candidate_notification: channelNotification,
              };
              const activityLog = extractActivityLogFromResponse(historyAsActivityLog, language);

              if (!activityLog) return undefined;

              return {
                ...activityLog,
                content: {
                  subject: sanitizeEmailHtml(channelNotification.subject || ''),
                  message: sanitizeEmailHtml(channelNotification.message || ''),
                  signoff: sanitizeEmailHtml(channelNotification.sign_off || ''),
                },
                ats: history.ats,
                messageReceived: false,
              } satisfies CandidateHistory;
            });
          }

          const historyAsActivityLog: CandidateActivityLogResponse = {
            ...history,
            created_at: history.date,
          };
          const activityLog = extractActivityLogFromResponse(historyAsActivityLog, language);

          if (!activityLog) return undefined;

          const hasContent =
            (typeof history.candidate_notification === 'object' &&
              Object.keys(history.candidate_notification).length > 0) ||
            history.content.length > 0;

          return {
            ...activityLog,
            ...(hasContent && {
              content: {
                subject: sanitizeEmailHtml(history.subject || history.candidate_notification?.subject || ''),
                message: sanitizeEmailHtml(history.content || history.candidate_notification?.message || ''),
                signoff: sanitizeEmailHtml(history.candidate_notification?.sign_off || ''),
              },
            }),
            ats: history.ats,
            messageReceived: [
              CandidateHistoryAction.EmailReceived,
              CandidateHistoryAction.InMailReceived,
              CandidateHistoryAction.IndeedReceived,
              CandidateHistoryAction.LinkedinReceived,
              CandidateHistoryAction.CandidateAppliedRecommendation,
            ].includes(history.action),
          } satisfies CandidateHistory;
        })
        .filter(Boolean) as CandidateHistory[];

      return {
        data: transformedData,
        indeedSyncMetadata: {
          syncedStatus,
          syncedAt,
          hasMessages,
        },
      };
    },
    {
      enabled: !!candidate && options.enabled,
      initialDataUpdatedAt: 0,
      initialData: {
        data: [],
        indeedSyncMetadata: {
          syncedStatus: SyncStatus.Pending,
          syncedAt: null,
          hasMessages: false,
        },
      },
    },
  );

  const resync = () => {
    const queryKey = [
      QueryKey.useCandidateHistoryQuery,
      {
        candidateEsPersonId: candidate?.es_person_id,
        status: candidate?.status,
      },
    ];

    queryClient.setQueryData(queryKey, {
      data: [],
      indeedSyncMetadata: {
        syncedStatus: SyncStatus.Pending,
        syncedAt: null,
        hasMessages: false,
      },
    });
    queryClient.refetchQueries({ queryKey });
  };

  return {
    data: queryResult.data?.data || [],
    isLoading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
    resync,
    indeedSyncMetadata: queryResult.data?.indeedSyncMetadata || {
      syncedStatus: SyncStatus.Pending,
      syncedAt: null,
      hasMessages: false,
    },
  };
};
